import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Heart, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatMoney(n) {
  return `₦${Number(n || 0).toLocaleString()}`;
}

function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (new Date() - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Continuous horizontal-scrolling ticker of recent successful donations.
 *
 * Why a ticker (rather than the existing static "Recent donors" list further
 * down the page): social proof has a strong recency effect. Seeing names
 * scroll past — even slowly — converts undecided visitors better than a
 * static list, especially when their phone is the only screen they see.
 *
 * Implementation choices:
 *   • CSS keyframe animation drives the scroll, so the main thread stays
 *     idle. Pauses on hover so users can read what flew past.
 *   • The list is duplicated head-to-tail and scrolled by exactly -50% so
 *     when the first half exits, the second half is already in place — the
 *     loop is seamless without a JS rAF loop.
 *   • Polls every 30 s for fresh donations. New donors are surfaced because
 *     the duplicated array is rebuilt each poll.
 *   • Respects `prefers-reduced-motion` — falls back to a static list.
 */
export default function SupportersTicker({ pollMs = 30000 }) {
  const [items, setItems] = useState([]);
  const [reducedMotion, setReducedMotion] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/donations/recent?limit=12`);
        if (alive) setItems(Array.isArray(r.data) ? r.data : []);
      } catch {
        if (alive) setItems([]);
      }
    };
    fetchOnce();
    timerRef.current = setInterval(fetchOnce, pollMs);
    return () => {
      alive = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [pollMs]);

  if (items.length === 0) return null;

  // Duplicate the list so the marquee can loop seamlessly with one
  // -50% translateX cycle. Each duplicate carries a stable key so React
  // doesn't unmount the leftward instance while it's still on-screen.
  const doubled = [...items, ...items];
  // Approximate per-item width (Tailwind: 220 px). Speed: 30 px/sec.
  const totalWidth = items.length * 240;
  const duration = Math.max(20, Math.round(totalWidth / 30));

  return (
    <div className="bg-gradient-to-r from-rose-50 via-white to-rose-50 border border-rose-100 rounded-sm overflow-hidden mb-6 group" data-testid="supporters-ticker">
      <div className="flex items-center px-3 sm:px-4 py-2 gap-3">
        <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-rose-700 shrink-0">
          <Sparkles className="w-3 h-3" /> Recent supporters
        </span>
        <div className="flex-1 overflow-hidden relative">
          {/* Edge fades so the marquee dissolves into the gradient ends */}
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-rose-50 to-transparent z-10" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-rose-50 to-transparent z-10" />
          {reducedMotion ? (
            <div className="flex gap-4 overflow-x-auto scrollbar-hide" data-testid="supporters-static">
              {items.map((it, i) => (
                <SupporterChip key={it.donation_id || `${it.created_at}-${i}`} item={it} />
              ))}
            </div>
          ) : (
            <div
              className="flex gap-4 group-hover:[animation-play-state:paused] supporters-marquee"
              style={{ animation: `supporters-scroll ${duration}s linear infinite` }}
              data-testid="supporters-marquee"
            >
              {doubled.map((it, i) => (
                <SupporterChip
                  key={`${it.donation_id || it.created_at || ""}-${i < items.length ? "a" : "b"}`}
                  item={it}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Component-scoped keyframes — no global stylesheet edit required */}
      <style>{`
        @keyframes supporters-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

function SupporterChip({ item }) {
  return (
    <div className="flex items-center gap-2 shrink-0 bg-white/70 border border-rose-100 rounded-sm px-2.5 py-1.5">
      <Heart className="w-3 h-3 text-rose-600 shrink-0" fill="currentColor" />
      <span className="text-xs font-bold text-gray-900 font-body whitespace-nowrap">{item.donor_name}</span>
      <span className="text-[10px] text-rose-700 font-mono whitespace-nowrap">{formatMoney(item.amount)}</span>
      <span className="text-[10px] text-gray-400 font-body whitespace-nowrap">{formatRelative(item.verified_at)}</span>
    </div>
  );
}
